import {GenericOAuth2} from '@capacitor-community/generic-oauth2';
import {isPlatform} from '@ionic/react';
import {PublicClientApplication} from '@azure/msal-browser';
import {authApi} from './api.service';
import {getAppId} from '@utils/tools/appId';

const getMsRedirectUrl = (): string => {
    if (isPlatform('android')) return process.env.REACT_APP_MS_ANDROID_REDIRECT_SCHEME + '://oauth2redirect';
    if (isPlatform('capacitor')) return getAppId() + '://oauth2redirect';
    return window.location.origin + '/blank.html';
};

// Cache the initialization promise to prevent concurrent calls creating multiple instances
let _pcaPromise: Promise<PublicClientApplication> | null = null;

const getPca = (): Promise<PublicClientApplication> => {
    if (!_pcaPromise) {
        const pca = new PublicClientApplication({
            auth: {
                clientId: process.env.REACT_APP_AZURE_APP_ID!,
                authority: process.env.REACT_APP_AZURE_AUTHORITY!,
            },
        });
        _pcaPromise = pca.initialize().then(() => pca);
    }
    return _pcaPromise;
};

const MS_SCOPES = (process.env.REACT_APP_AZURE_SCOPES ?? '').split(' ').filter(Boolean);

const MS_POPUP_REQUEST = (loginHint?: string) => ({
    scopes: MS_SCOPES,
    redirectUri: window.location.origin + '/blank.html',
    loginHint,
});

// Web only: opens MS login popup and returns access token
export const getMsTokenWeb = async (loginHint?: string): Promise<string | null> => {
    // Clear any stale interaction lock left by a previous abandoned or page-refreshed popup.
    sessionStorage.removeItem('msal.interaction.status');
    try {
        const pca = await getPca();
        const result = await pca.loginPopup(MS_POPUP_REQUEST(loginHint));
        return result.accessToken ?? null;
    } catch {
        return null;
    }
};

// Native only: uses plugin popup flow
export const getMsToken = async (loginHint?: string): Promise<string | null> => {
    try {
        const result = await GenericOAuth2.authenticate({
            appId: process.env.REACT_APP_AZURE_APP_ID,
            authorizationBaseUrl: process.env.REACT_APP_AZURE_AUTHORIZE_ENDPOINT,
            accessTokenEndpoint: process.env.REACT_APP_AZURE_TOKEN_ENDPOINT,
            scope: process.env.REACT_APP_AZURE_SCOPES,
            redirectUrl: getMsRedirectUrl(),
            responseType: 'code',
            pkceEnabled: true,
            logsEnabled: false,
            additionalParameters: loginHint ? {login_hint: loginHint} : undefined,
        });
        return result?.access_token_response?.access_token ?? null;
    } catch {
        return null;
    }
};

export const initMsal = (): Promise<void> => getPca().then(() => undefined);

export const msConnect = async (msToken: string) => {
    const model = {
        msToken,
        client_id: process.env.REACT_APP_CLIENT_ID,
        client_secret: process.env.REACT_APP_CLIENT_SECRET,
    };
    return authApi.post('api/v2/connect/microsoft', model, { headers: { 'No-Loader': true } });
};

export const msLogout = async (): Promise<void> => {
    await GenericOAuth2.logout({
        appId: process.env.REACT_APP_AZURE_APP_ID,
        authorizationBaseUrl: process.env.REACT_APP_AZURE_AUTHORIZE_ENDPOINT,
    });
};
